import { useNavigate } from '@tanstack/react-router';
import {
  CircleUser,
  FileText,
  Home,
  Inbox,
  MessageSquare,
  Monitor,
  Moon,
  Plus,
  Search,
  Sun,
  Users,
} from 'lucide-react';
import { motion } from 'motion/react';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { useSearch } from '@/shared/api/generated/search/search';
import { unwrap } from '@/shared/api/unwrap';
import { t } from '@/shared/i18n';
import { cx } from '@/shared/lib/cx';
import { springs } from '@/shared/lib/motion';
import { useTheme } from '@/shared/lib/useTheme';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { toast } from '@/shared/ui/Toast';

import styles from './CommandPalette.module.scss';
import { usePalette } from './palette';

/**
 * Палитра ⌘K (roadmap 7.2): одно поле — и поиск, и действия.
 *
 * Пока поле пустое — действия (перейти, тема, создать задачу). Начал
 * печатать — сервер ищет по задачам, страницам, комментариям, людям и
 * проектам, а действия остаются сверху, если совпадают по названию.
 * Один плоский список: стрелки ходят по нему насквозь, Enter выбирает.
 * Подложка под выбранным пунктом одна и переезжает на пружине — как в
 * меню.
 */

type Group = keyof typeof t.palette.groups;

interface Item {
  id: string;
  group: Group;
  icon: ReactNode;
  label: ReactNode;
  /** Текст для совпадения по подстроке — у действий. */
  text?: string;
  /** Серым справа или под названием: где лежит, кто написал. */
  hint?: ReactNode;
  /** Фрагмент текста с подсветкой совпадения. */
  snippet?: string | undefined;
  run: () => void | Promise<void>;
}

const GROUP_ORDER: Group[] = ['actions', 'tasks', 'pages', 'comments', 'people', 'spaces'];

/** Пауза между набором и запросом: сервер не должен получать каждую букву. */
const DEBOUNCE_MS = 150;

/**
 * Фрагмент от сервера: совпадения обёрнуты в [[ ]]. Рисуем их отметкой,
 * а не dangerouslySetInnerHTML — текст пришёл от людей.
 */
function Snippet({ text }: { text: string }) {
  const parts = text.split(/\[\[|\]\]/);

  return (
    <span className={styles['snippet']}>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <mark key={`${String(index)}-${part}`} className={styles['mark']}>
            {part}
          </mark>
        ) : (
          <span key={`${String(index)}-${part}`}>{part}</span>
        ),
      )}
    </span>
  );
}

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debounced;
}

export function CommandPalette({ workspace }: { workspace: string }) {
  const open = usePalette((s) => s.open);
  const setOpen = usePalette((s) => s.setOpen);

  // Содержимое живёт только пока окно открыто: Radix размонтирует его при
  // закрытии, и следующее открытие начинается с чистого поля само собой.
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent bare size="md" title={t.palette.title} className={styles['palette']}>
        <PaletteBody workspace={workspace} />
      </DialogContent>
    </Dialog>
  );
}

function PaletteBody({ workspace }: { workspace: string }) {
  const setOpen = usePalette((s) => s.setOpen);
  const createTask = usePalette((s) => s.createTask);
  const navigate = useNavigate();
  const { setMode } = useTheme();

  const [query, setQuery] = useState('');
  // Выбранный пункт — по запросу: новый запрос начинается с первого.
  const [cursor, setCursor] = useState(0);
  const [busy, setBusy] = useState(false);
  const debounced = useDebounced(query.trim(), DEBOUNCE_MS);

  const results = useSearch(
    workspace,
    { q: debounced },
    {
      query: {
        enabled: debounced !== '',
        select: unwrap,
        // Прежний ответ остаётся на экране, пока едет новый: список не
        // должен мигать пустотой на каждую букву.
        placeholderData: (previous) => previous,
        staleTime: 30_000,
      },
    },
  );

  const close = () => {
    setOpen(false);
  };

  const go = (to: string, params: Record<string, string>, search?: Record<string, string>) => {
    close();
    void navigate({ to, params, search: search ?? {} } as never);
  };

  const actions = useMemo<Item[]>(() => {
    const trimmed = query.trim();
    const list: Item[] = [];

    if (createTask && trimmed !== '') {
      list.push({
        id: 'create-task',
        group: 'actions',
        icon: <Plus />,
        label: t.palette.actions.createTask(trimmed),
        hint: t.palette.actions.createTaskHint,
        run: async () => {
          setBusy(true);

          try {
            const key = await createTask(trimmed);

            close();
            void navigate({ search: (prev: object) => ({ ...prev, task: key }) } as never);
          } catch {
            toast.error(t.common.unknownError);
          } finally {
            setBusy(false);
          }
        },
      });
    }

    const nav: Item[] = [
      {
        id: 'home',
        group: 'actions',
        icon: <Home />,
        label: t.palette.actions.home,
        text: t.palette.actions.home,
        run: () => {
          go('/app/w/$slug', { slug: workspace });
        },
      },
      {
        id: 'inbox',
        group: 'actions',
        icon: <Inbox />,
        label: t.palette.actions.inbox,
        text: t.palette.actions.inbox,
        run: () => {
          go('/app/w/$slug/inbox', { slug: workspace });
        },
      },
      {
        id: 'members',
        group: 'actions',
        icon: <Users />,
        label: t.palette.actions.members,
        text: t.palette.actions.members,
        run: () => {
          go('/app/w/$slug/members', { slug: workspace });
        },
      },
      {
        id: 'profile',
        group: 'actions',
        icon: <CircleUser />,
        label: t.palette.actions.profile,
        text: t.palette.actions.profile,
        run: () => {
          go('/app/profile', {});
        },
      },
      {
        id: 'theme-dark',
        group: 'actions',
        icon: <Moon />,
        label: t.palette.actions.themeDark,
        text: t.palette.actions.themeDark,
        run: () => {
          setMode('dark');
          close();
        },
      },
      {
        id: 'theme-light',
        group: 'actions',
        icon: <Sun />,
        label: t.palette.actions.themeLight,
        text: t.palette.actions.themeLight,
        run: () => {
          setMode('light');
          close();
        },
      },
      {
        id: 'theme-system',
        group: 'actions',
        icon: <Monitor />,
        label: t.palette.actions.themeSystem,
        text: t.palette.actions.themeSystem,
        run: () => {
          setMode('system');
          close();
        },
      },
    ];

    // Действия по подстроке названия — без сервера; при пустом поле все.
    const needle = trimmed.toLowerCase();

    for (const item of nav) {
      if (needle === '' || (item.text ?? '').toLowerCase().includes(needle)) {
        list.push(item);
      }
    }

    return list;
    // navigate и setMode стабильны; close — замыкание без состояния.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, createTask, workspace]);

  const found = useMemo<Item[]>(() => {
    const data = debounced === '' ? undefined : results.data;

    if (!data) {
      return [];
    }

    const list: Item[] = [];

    for (const task of data.tasks ?? []) {
      list.push({
        id: `task-${task.key}`,
        group: 'tasks',
        icon: (
          <span
            className={cx(styles['dot'], task.is_done && styles['dot--done'])}
            style={{ background: `#${task.status_color}` }}
          />
        ),
        label: task.title,
        hint: (
          <>
            <span className={styles['key']}>{task.key}</span>
            {task.status_name}
          </>
        ),
        snippet: task.snippet || undefined,
        run: () => {
          go(
            '/app/w/$slug/s/$space/b/$board',
            { slug: workspace, space: task.space_slug, board: task.board_slug },
            { task: task.key },
          );
        },
      });
    }

    for (const page of data.pages ?? []) {
      list.push({
        id: `page-${page.id}`,
        group: 'pages',
        icon:
          page.icon !== '' ? <span className={styles['emoji']}>{page.icon}</span> : <FileText />,
        label: page.title,
        hint: page.space_slug === '' ? t.palette.pageOfWorkspace : page.space_slug,
        snippet: page.snippet || undefined,
        run: () => {
          if (page.space_slug === '') {
            go('/app/w/$slug/p/$pageId', { slug: workspace, pageId: page.id });
          } else {
            go('/app/w/$slug/s/$space/p/$pageId', {
              slug: workspace,
              space: page.space_slug,
              pageId: page.id,
            });
          }
        },
      });
    }

    for (const comment of data.comments ?? []) {
      list.push({
        id: `comment-${comment.id}`,
        group: 'comments',
        icon: <MessageSquare />,
        label: (
          <>
            <span className={styles['muted']}>{t.palette.commentBy(comment.author_name)} </span>
            <span className={styles['key']}>{comment.task_key}</span> {comment.task_title}
          </>
        ),
        snippet: comment.snippet,
        run: () => {
          go(
            '/app/w/$slug/s/$space/b/$board',
            { slug: workspace, space: comment.space_slug, board: comment.board_slug },
            { task: comment.task_key },
          );
        },
      });
    }

    for (const person of data.people ?? []) {
      list.push({
        id: `person-${person.id}`,
        group: 'people',
        icon: <CircleUser />,
        label: person.name,
        hint: person.email,
        run: () => {
          go('/app/w/$slug/members', { slug: workspace });
        },
      });
    }

    for (const space of data.spaces ?? []) {
      list.push({
        id: `space-${space.slug}`,
        group: 'spaces',
        icon: <span className={styles['dot']} style={{ background: `#${space.color}` }} />,
        label: space.name,
        run: () => {
          go('/app/w/$slug/s/$space', { slug: workspace, space: space.slug });
        },
      });
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.data, debounced, workspace]);

  const items = useMemo(() => [...actions, ...found], [actions, found]);

  // Список мог укоротиться, пока курсор стоял ниже, — не выходим за край.
  const active = Math.min(cursor, Math.max(items.length - 1, 0));
  const setActive = setCursor;

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (items.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((i) => (i + 1) % items.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((i) => (i - 1 + items.length) % items.length);
        break;
      case 'Enter': {
        event.preventDefault();

        const item = items[active];

        if (item && !busy) {
          void item.run();
        }

        break;
      }
      default:
        break;
    }
  };

  const searching = debounced !== '' && results.isFetching && results.data === undefined;
  const nothing = debounced !== '' && !searching && items.length === 0;

  return (
    <>
      <div className={styles['field']}>
        <Search className={styles['field__icon']} />
        <input
          className={styles['input']}
          value={query}
          placeholder={t.palette.placeholder}
          aria-label={t.palette.title}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          onChange={(event) => {
            setQuery(event.target.value);
            setCursor(0);
          }}
          onKeyDown={onKeyDown}
        />
        {results.isFetching && debounced !== '' && (
          <span className={styles['spinner']} aria-hidden="true" />
        )}
      </div>

      <List
        items={items}
        active={active}
        busy={busy}
        onHover={setActive}
        empty={nothing ? t.palette.empty : searching ? t.palette.searching : undefined}
      />

      <footer className={styles['footer']}>
        <span>
          <kbd>↑↓</kbd> {t.palette.keys.navigate}
        </span>
        <span>
          <kbd>↵</kbd> {t.palette.keys.open}
        </span>
        <span>
          <kbd>esc</kbd> {t.palette.keys.close}
        </span>
      </footer>
    </>
  );
}

function List({
  items,
  active,
  busy,
  onHover,
  empty,
}: {
  items: Item[];
  active: number;
  busy: boolean;
  onHover: (index: number) => void;
  empty: string | undefined;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ top: number; height: number } | null>(null);

  // Подложка едет к выбранному пункту; сам пункт при этом остаётся в
  // видимой части списка.
  useLayoutEffect(() => {
    const root = listRef.current;
    const node = root?.querySelector<HTMLElement>(`[data-index="${String(active)}"]`);

    if (!root || !node) {
      setBox(null);

      return;
    }

    setBox({ top: node.offsetTop, height: node.offsetHeight });
    node.scrollIntoView({ block: 'nearest' });
  }, [active, items]);

  if (items.length === 0) {
    return <div className={styles['empty']}>{empty ?? t.palette.hint}</div>;
  }

  const firstOfGroup = (group: Group) => items.findIndex((item) => item.group === group);

  return (
    <div ref={listRef} className={styles['list']} role="listbox">
      {box && (
        <motion.span
          className={styles['highlight']}
          initial={false}
          animate={{ y: box.top, height: box.height }}
          transition={springs.rubber}
          aria-hidden="true"
        />
      )}
      {GROUP_ORDER.flatMap((group) =>
        items
          .map((item, index) => ({ item, index }))
          .filter(({ item }) => item.group === group)
          .map(({ item, index }) => {
            const heading = firstOfGroup(group) === index;

            return (
              <div key={item.id}>
                {heading && <div className={styles['group']}>{t.palette.groups[group]}</div>}
                <button
                  type="button"
                  role="option"
                  aria-selected={index === active}
                  data-index={index}
                  className={cx(styles['item'], index === active && styles['item--active'])}
                  disabled={busy}
                  onMouseMove={() => {
                    if (index !== active) {
                      onHover(index);
                    }
                  }}
                  onClick={() => {
                    void item.run();
                  }}
                >
                  <span className={styles['item__icon']}>{item.icon}</span>
                  <span className={styles['item__body']}>
                    <span className={styles['item__row']}>
                      <span className={styles['item__label']}>{item.label}</span>
                      {item.hint !== undefined && (
                        <span className={styles['item__hint']}>{item.hint}</span>
                      )}
                    </span>
                    {item.snippet !== undefined && <Snippet text={item.snippet} />}
                  </span>
                </button>
              </div>
            );
          }),
      )}
    </div>
  );
}
